import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';

/**
 * "Forgot password" — asks the API to send a reset link.
 *
 * The response is always the same regardless of whether the email
 * exists, so we always show the same success message. No enumeration
 * signal.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api('/api/v1/auth/password/reset/request', {
        method: 'POST',
        body: { email },
        skipAuthRetry: true,
      });
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send the email. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="mx-auto mt-24 max-w-sm px-4">
        <h1 className="mb-4 text-2xl font-semibold">Check your inbox</h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          If an account exists for <strong>{email}</strong>, we've sent a
          password reset link. It expires in 30 minutes.
        </p>
        <p className="mt-4 text-sm text-slate-500">
          <Link to="/login" className="underline">Back to sign in</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-24 max-w-sm px-4">
      <h1 className="mb-2 text-2xl font-semibold">Reset password</h1>
      <p className="mb-6 text-sm text-slate-500">
        Enter your email and we'll send a link to choose a new password.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <input
          type="email" required autoComplete="username" placeholder="Email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-3 py-2 outline-none focus:border-slate-500 dark:border-slate-600 dark:bg-slate-800"
        />
        {error && <p className="text-sm text-red-700">{error}</p>}
        <button
          type="submit" disabled={submitting}
          className="w-full rounded bg-slate-900 py-2 font-medium text-white disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900"
        >
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
      <p className="mt-4 text-sm text-slate-500">
        <Link to="/login" className="underline">Back to sign in</Link>
      </p>
    </div>
  );
}
